/** 1 = PatientAndSharedCaseQuery 2 = DataBlockQuery 3 = VisitQuery 4 = PatientQuery 5 = CaseQuery */
export enum MergeFieldQueryType {
  DataBlockQuery = 2,
  VisitQuery = 3,
  PatientQuery = 4,
  CaseQuery = 5,
  HL7Settings = 6,
  FileQuery = 7,
  GDTSettings = 8,
}

export type QueryWithParameters = {
  query: {
    /** Type of a Query this MergeField refers to */
    queryType: MergeFieldQueryType;
    /** Id of DataBlockType (if MergeFieldQueryType query is DataBlockQuery, null otherwise (e.g. PatientWithSharedCaseFieldsDocumentQuery))  */
    dataBlockTypeName: string | null;

    /** Ids of Visits (if selectedVisits function was used in Expression)  */
    selectedVisits?: number[] | null;
  };
  filter: string | null;
  limit: ExpressionDataFunction | null;

  // Define how to get the data from expression.
  // We usually get the data by calling a function.
  // E.g. lastBlockInVisit('PSG').
  // For the example above, expressionFunctionName = 'lastBlockInVisit', expressionFunctionArgument = 'PSG'
  expressionFunctionName: ExpressionDataFunction;
  expressionFunctionArgument: string;
  expressionFunctionArgument2?: string | null;

  /*
   * Fields that were accessed (e.g. `patient()['lastName'] + ' ' + patient()['firstName']` will give ['lastName', 'firstName']
   * This is to have an ability to optimize SELECT queries to only request needed fields
   */
  fields: string[];
};

export enum ExpressionDataFunction {
  'lastBlockInVisit' = 'lastBlockInVisit',
  'firstBlockInVisit' = 'firstBlockInVisit',
  'nthBlockInVisit' = 'nthBlockInVisit',
  'nthBlockInCase' = 'nthBlockInCase',
  'lastBlockInCase' = 'lastBlockInCase',
  'firstBlockInCase' = 'firstBlockInCase',
  'allVisits' = 'allVisits',
  'allBlocksInCase' = 'allBlocksInCase',
  'allBlocksInVisit' = 'allBlocksInVisit',
  'block' = 'block',
  'previousVisit' = 'previousVisit',
  'nextVisit' = 'nextVisit',
  'previousBlock' = 'previousBlock',
  'selectedVisits' = 'selectedVisits',
  'Case' = 'Case',
  'visit' = 'visit',
  'patient' = 'patient',
  'hl7' = 'hl7',
  'gdt' = 'gdt',
  'file' = 'file',
  'lastVisitInCase' = 'lastVisitInCase',
  'firstVisitInCase' = 'firstVisitInCase',
}

export type ExpressionFunctionsCachedResult = {
  setValue(
    func: ExpressionDataFunction,
    argument: string | undefined,
    value: any
  ): ExpressionFunctionsCachedResult;
} & Record<ExpressionDataFunction, Record<string, any>>;

export enum SexType {
  Undefined = 0,
  Male = 1,
  Female = 2,
}

export interface DataBlockTableColumnDto {
  id: number;
  name: string;
  dataType: DataType;
  dictionaryId: number | null;
  dictionaryColumnId: number | null;
  order: number;
  dictionaryColumnName?: string | null;
  /** Whether it's allowed to enter values not present in a Dictionary or not.
Only applicable to Fields with DataType of Dictionary.
Similar to IsOptionAllowed. */
  isOptionAllowed: boolean;
  /** Could be set for Boolean fields.
If true it means that only a single Row could have this column set to True
(basically we should show Radio Buttons instead of Checkboxes) */
  isSingleValue: boolean;
}

export enum DataType {
  Undefined = 0,
  Number = 1,
  Text = 2,
  Boolean = 3,
  DateTime = 4,
  DateOnly = 5,
  TimeOnly = 6,
  Dictionary = 7,
  Table = 8,
  MultilineText = 9,
}

export interface DocumentQueryFieldDto {
  dataField: string;
  clrPropertyName: string;
  caption?: string | null;
  dataType: DataType;
  hideFromFilter: boolean;
  /** Contains JSON-serialized Dx FilterExpression, to be evaluated on DataSource of applied filters.
Expression will be evaluated on the client, and this field will only be shown if ShowCondition is true.
(needed for showing MeasurementFields only when appropriate MeasurementType is selected)
e.g. ['measurement.TypeId', '=', '3'] */
  showCondition?: any[] | null;
  possibleValues?: { [key: string]: any };
  nestedFields?: DocumentQueryFieldDto[] | null;
  tableColumns?: DataBlockTableColumnDto[] | null;
}

export interface MergeFieldQueryDto {
  /** Type of a Query this MergeField refers to */
  queryType: MergeFieldQueryType;
  /** Id of DataBlockType (if MergeFieldQueryType query is DataBlockQuery, null otherwise (e.g. PatientWithSharedCaseFieldsDocumentQuery)) */
  dataBlockTypeId: number | null;
  /** Ids of Visits (if selectedVisits function was used in Expression) */
  selectedVisits?: number[] | null;
}

export interface DocumentQueryDto {
  queryIdentifier: MergeFieldQueryDto;
  queryFields: DocumentQueryFieldDto[];
}

export interface ExpressionFunctionsDto {
  data: Record<string, any>;
  currentBaseQuery: DocumentQueryDto;
  query: QueryWithParameters;
}
